import React, { useState, useEffect, useCallback, useRef } from 'react';
import { miniERPOfflineDB } from '../utils/miniERPOfflineDB';

// navigator.onLine is unreliable on flaky mobile networks — trusted only as
// a UI hint. The real decision is always: did the live request actually fail?
const isNetworkFailure = (err) => !err?.response && !err?.status;
const isForcedOffline = () => localStorage.getItem("FORCE_OFFLINE") === "1";
const isAppOnline = () => navigator.onLine && !isForcedOffline();

export function useOfflineCounterMemo({ createPettyTransaction, fetchPettyTransactions, userId }) {
  // const [browserOnline, setBrowserOnline] = useState(navigator.onLine);
  const [browserOnline, setBrowserOnline] = useState(isAppOnline());

  useEffect(() => {
    const syncState = () => setBrowserOnline(isAppOnline());

    window.addEventListener("online", syncState);
    window.addEventListener("offline", syncState);
    window.addEventListener("storage", syncState);

    return () => {
      window.removeEventListener("online", syncState);
      window.removeEventListener("offline", syncState);
      window.removeEventListener("storage", syncState);
    };
  }, []);

  const [records, setRecords] = useState([]); // merged local mirror — the single source the list renders from
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const uploadingRef = useRef(false);
  const downloadingRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!miniERPOfflineDB.isSupported || !userId) return;
    const rows = await miniERPOfflineDB.getAllForUser(userId);
    setRecords(rows);
  }, [userId]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const onOnline = () => { setBrowserOnline(true); uploadToOnline(); };
    const onOffline = () => setBrowserOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Create a new entry — always tries the live save first regardless of the
  // browser's online flag; only falls back to the local queue on a genuine
  // network failure. A successful live save is ALSO mirrored locally so it
  // shows up immediately in the merged list with an "Online" marker.
  const submitEntry = useCallback(async (payload) => {
    try {
      if (isForcedOffline()) {
        throw new Error("FORCED_OFFLINE");
      }
      const res = await createPettyTransaction(payload);
      if (res?.data) await miniERPOfflineDB.upsertOnlineSnapshot(userId, res.data);
      await refresh();
      return { queued: false, data: res?.data };
    } catch (err) {
      // if (!isNetworkFailure(err)) throw err; // real validation/server error — surface it, don't queue
      if (err.message !== "FORCED_OFFLINE" && !isNetworkFailure(err)) {
        throw err;
      }
      const record = await miniERPOfflineDB.addOfflineEntry(userId, payload);
      await refresh();
      return { queued: true, data: record };
    }
  }, [createPettyTransaction, userId, refresh]);

  // "Sync and Upload Online" — push every locally-created entry the server
  // doesn't have yet.
  const uploadToOnline = useCallback(async () => {
    if (uploadingRef.current || !miniERPOfflineDB.isSupported || !userId) return;
    uploadingRef.current = true;
    setUploading(true);
    try {
      const rows = await miniERPOfflineDB.getAllForUser(userId);
      const unsynced = rows.filter((r) => r.origin === 'offline' && r.syncStatus !== 'synced');
      for (const row of unsynced) {
        await miniERPOfflineDB.markStatus(row.clientId, { syncStatus: 'syncing' });
        try {
          const res = await createPettyTransaction({ ...row.payload, clientId: row.clientId });
          await miniERPOfflineDB.markStatus(row.clientId, { syncStatus: 'synced', serverId: res?.data?._id || '', syncError: '' });
        } catch (err) {
          if (isNetworkFailure(err)) {
            await miniERPOfflineDB.markStatus(row.clientId, { syncStatus: 'pending' });
            break; // still no real connection — stop, leave the rest pending
          }
          await miniERPOfflineDB.markStatus(row.clientId, { syncStatus: 'failed', syncError: err?.message || 'Sync failed' });
        }
      }
    } finally {
      await refresh();
      uploadingRef.current = false;
      setUploading(false);
    }
  }, [createPettyTransaction, userId, refresh]);

  // "Sync and Download from Online" — pull anything the server has (e.g.
  // entered from the regular Home page) that isn't mirrored locally yet.
  const downloadFromOnline = useCallback(async () => {
    if (downloadingRef.current || !miniERPOfflineDB.isSupported || !userId) return;
    downloadingRef.current = true;
    setDownloading(true);
    try {
      const knownServerIds = await miniERPOfflineDB.getKnownServerIds(userId);
      const res = await fetchPettyTransactions();
      const serverRows = res?.data || [];
      const missing = serverRows.filter((r) => !knownServerIds.has(r._id));
      for (const r of missing) {
        await miniERPOfflineDB.upsertOnlineSnapshot(userId, r);
      }
      await miniERPOfflineDB.pruneOldSynced(userId);
      return { pulled: missing.length };
    } finally {
      await refresh();
      downloadingRef.current = false;
      setDownloading(false);
    }
  }, [fetchPettyTransactions, userId, refresh]);

  const unsyncedCount = records.filter((r) => r.origin === 'offline' && r.syncStatus !== 'synced').length;

  // "Clear from Offline" — wipes only this device's local mirror. Caller is
  // responsible for confirming with the user; this just reports whether
  // it's currently safe to do so (nothing unsynced would be lost).
  const clearOffline = useCallback(async () => {
    await miniERPOfflineDB.clearAllForUser(userId);
    await refresh();
  }, [userId, refresh]);

  return {
    browserOnline, records, unsyncedCount, uploading, downloading,
    submitEntry, uploadToOnline, downloadFromOnline, clearOffline, refresh,
    isSupported: miniERPOfflineDB.isSupported,
  };
}